import {
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioPaymentFrequencyEnum,
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
	type ClientPropertyCottageDetailResponseDTO,
	type ClientPropertyFlatDetailResponseDTO,
	type ClientPropertyHouseDetailResponseDTO,
	type PortfolioDetailGraphDataDTO,
	type PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { PageResources } from "web_myplann/Portfolio/Components/PropertySections/House";
import type { DayData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import {
	dateFormatter,
	moneyFormatter,
} from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

export const getBuildingMaterial = (
	data:
		| ClientPropertyHouseDetailResponseDTO
		| ClientPropertyCottageDetailResponseDTO
		| ClientPropertyFlatDetailResponseDTO,
	t: (key: string) => string,
) => {
	return data?.buildingMaterial === ClientPropertyBuildingMaterialEnum.Custom
		? data.customBuildingMaterial
		: data?.buildingMaterial
			? t(PageResources.Property.BuildingMaterialValue[data.buildingMaterial])
			: "";
};

export const getDisposition = (
	data:
		| ClientPropertyHouseDetailResponseDTO
		| ClientPropertyCottageDetailResponseDTO
		| ClientPropertyFlatDetailResponseDTO,
	t: (key: string) => string,
) => {
	return data?.disposition === ClientPropertyBuildingDispositionEnum.Custom
		? data.customDisposition
		: data?.disposition
			? t(PageResources.Property.DispositionValue[data.disposition])
			: "";
};

export const getPortfolioValueDate = (
	daysData: DayData[],
	baseData: PortfolioDetailResponseDTO,
) =>
	daysData.length > 0
		? dateFormatter(baseData.portfolioValueDate ?? new Date())
		: EMPTY_DETAIL_FIELD;

export const calculatePaidAll = (baseData: PortfolioDetailResponseDTO) =>
	baseData.tools.reduce((acc, tool) => acc + tool.monthlyPayment, 0);

const checkToolHasActivePurchase = (
	tool: PortfolioDetailResponseDTO["tools"][0],
): boolean => {
	const hasActiveCommodityPurchase = tool.commodities?.some((commodity) => {
		const isOneTime =
			commodity.frequency === ClientPortfolioInvestmentFrequencyEnum.Once;
		const isPurchase =
			commodity.investmentType === undefined ||
			commodity.investmentType === null ||
			(commodity.investmentType as string) === "Buy";
		const isActive = commodity.startAt
			? getIsActive(commodity.startAt, commodity.endAt, isOneTime)
			: false;

		return !isOneTime && isPurchase && isActive;
	});

	if (hasActiveCommodityPurchase) return true;

	const hasActiveCryptoPurchase = tool.cryptocurrencyInstructions?.some(
		(instruction) => {
			const isOneTime =
				instruction.frequency === ClientPortfolioInvestmentFrequencyEnum.Once;
			const isPurchase =
				instruction.investmentType === undefined ||
				instruction.investmentType === null ||
				(instruction.investmentType as string) === "Buy";
			const isActive = instruction.date
				? getIsActive(instruction.date, instruction.endedAt, isOneTime)
				: false;

			return !isOneTime && isPurchase && isActive;
		},
	);

	if (hasActiveCryptoPurchase) return true;

	const hasActiveInvestmentPurchase = tool.investments?.some((investment) => {
		const isOneTime =
			investment.frequency === ClientPortfolioInvestmentFrequencyEnum.Once;
		const isPurchase =
			investment.investmentType === undefined ||
			investment.investmentType === null ||
			(investment.investmentType as string) === "Buy";
		const isActive = investment.startAt
			? getIsActive(investment.startAt, investment.endAt, isOneTime)
			: false;

		return !isOneTime && isPurchase && isActive;
	});

	return hasActiveInvestmentPurchase;
};

export const hasActivePeriodicPurchaseInstructions = (
	baseData: PortfolioDetailResponseDTO,
): boolean => {
	return baseData.tools.some((tool) => checkToolHasActivePurchase(tool));
};

export const toolHasActivePeriodicPurchaseInstructions = (
	tool: PortfolioDetailResponseDTO["tools"][0],
): boolean => {
	return checkToolHasActivePurchase(tool);
};

export const getIsActive = (
	startAt: string | undefined | null,
	endAt: string | undefined | null,
	isOneTime: boolean,
) => {
	const now = new Date();
	const startDate = startAt ? new Date(startAt) : now;

	const todayDateOnly = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
	);
	const startDateOnly = new Date(
		startDate.getFullYear(),
		startDate.getMonth(),
		startDate.getDate(),
	);

	if (isOneTime) {
		return todayDateOnly.getTime() === startDateOnly.getTime();
	}

	const hasStarted = startDateOnly <= todayDateOnly;

	if (!endAt) {
		return hasStarted;
	}

	const endDate = new Date(endAt);
	const endDateOnly = new Date(
		endDate.getFullYear(),
		endDate.getMonth(),
		endDate.getDate(),
	);

	return hasStarted && todayDateOnly <= endDateOnly;
};

export const propertyCarTitle = (
	carBrand: string | null | undefined,
	carModel: string | null | undefined,
	carLicensePlate: string | null | undefined,
) => {
	const brandModel = [carBrand, carModel].filter(Boolean).join(" ");
	const licensePlate = carLicensePlate ? `(${carLicensePlate})` : "";

	if (brandModel && licensePlate) {
		return `${brandModel} ${licensePlate}`;
	}

	if (brandModel) {
		return brandModel;
	}

	if (carLicensePlate) {
		return carLicensePlate;
	}

	return "";
};

export const generateGraphData = (): PortfolioDetailGraphDataDTO => {
	const startDate = new Date("2018-01-01");
	const today = new Date();
	const daysData = [];

	let totalValue = 10000;
	let totalInvestedValue = 5000;
	let enterFee = 0;

	for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 7)) {
		totalValue += Math.floor(Math.random() * 1000 - 100);
		totalInvestedValue += Math.floor(Math.random() * 500);
		enterFee = Math.random() < 0.1 ? Math.floor(Math.random() * 100) : 0;

		daysData.push({
			date: d.toISOString().slice(0, 10),
			total: {
				value: totalValue,
				investedValue: totalInvestedValue,
				enterFee,
			},
		});
	}

	return {
		daysData,
		totalValue,
		totalInvestedValue,
	};
};

export const getPortfolioFrequencyValue = (
	value: number | null | undefined,
	currencySign: string,
	paymentFrequency: ClientPortfolioPaymentFrequencyEnum | null,
	t: (key: string) => string,
	decimal?: number,
) =>
	value
		? `${moneyFormatter(value, currencySign, undefined, decimal)} ${t(Resources.PortfolioDetail.SharedLabels.Frequency[paymentFrequency ?? ClientPortfolioPaymentFrequencyEnum.None]).toLowerCase()}`
		: EMPTY_DETAIL_FIELD;

export const maturityFormatter = (
	months: number,
	t: (key: string, options: { count: number }) => string,
) => {
	const years = Math.floor(months / 12);
	const remMonths = months % 12;

	const parts: string[] = [];

	if (years > 0)
		parts.push(
			t(Resources.PortfolioDetail.Loan.Frequency.Years, { count: years }),
		);

	if (remMonths > 0)
		parts.push(
			t(Resources.PortfolioDetail.Loan.Frequency.Months, {
				count: remMonths,
			}),
		);

	return parts.join(" ");
};
